import { Component, Vue, Prop } from 'vue-property-decorator';
import { rpcClient } from '@/support/Clients/RpcClient';
import { AxiosError, AxiosResponse } from 'axios';

@Component<MiFileUpload>({
  filters: {
    calculateKB: (value: number) => (value * 0.001).toFixed(1),
    concatenateName: (value: string) => {
      if (value.length >= 25) {
        return `${value.slice(0, 15)}...${value.slice(value.length - 10, value.length)}`;
      }
      return value;
    },
  },
})
export default class MiFileUpload extends Vue {
  @Prop()
  protected acceptedFileTypes?: string[];

  protected accepted: string [] = [];

  @Prop({ default: 300 })
  protected height?: number;

  @Prop()
  protected filePath?: string;

  @Prop({ default: false })
  protected clearAfterUpload?: boolean;

  @Prop({ default: false })
  protected multiple?: boolean;

  @Prop({ default: 'add_photo_alternate' })
  protected fileIcon?: string;

  //
  // STATES
  //
  protected dragging = false;

  protected hovering = false;

  protected isUploaded = false;

  protected hasError = false;

  protected invalidFileType = false;

  protected isUploading = false;

  protected showRipple = false;

  protected style: RippleStyle = {};

  // Files
  protected files: File[] = [];

  //
  // TO TRACK IF A FILE IS UPLOADED OR NOT: MULTIPLE ONLY
  //
  protected filesUploaded: FilesUpload[] = [];

  //
  // PARSE CORRECT ICON FOR FILE TYPE
  //
  protected imageExtesions = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'bmp'];

  protected fileIcons: {[key:string]:string} = {
    pdf: 'mdi-file-pdf-box',
    xls: 'mdi-file-excel-box',
    xlsx: 'mdi-file-excel-box',
    docx: 'mdi-file-word-box',
    doc: 'mdi-file-word-box',
    ppt: 'mdi-file-powerpoint-box',
    pptx: 'mdi-file-powerpoint-box',
    other: 'mdi-file-document-box',
    jpg: 'mdi-image',
    jpeg: 'mdi-image',
    png: 'mdi-image',
  };

  protected mounted(): void {
    if (! this.acceptedFileTypes) {
      this.accepted = [
        'image/png',
        'image/jpeg',
        'image/svg+xml',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf',
        'msg',
        'oft',
      ];
    } else {
      this.accepted = this.acceptedFileTypes;
    }
  }

  //
  // GET FILE KEY
  //
  protected async getFileKey(files: File[]): Promise<SubmitResponse[] | null> {
    const submitResponse: SubmitResponse[] = [];

    // FILL FILEUPLOADED OBJECT
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      //
      // VALIDATE FILE
      //
      this.invalidFileType = ! this.accepted.includes(file.type.toString());
      if (this.invalidFileType) {
        this.dragging = true;
        return null;
      }

      const parsedPath = file.name.split('.').pop();

      this.filesUploaded.push({
        isLoading: true,
        icon: parsedPath ? parsedPath.split('?')[0] : 'other',
      });
    }

    //
    // START RECIEVING THE S3 KEYS
    //
    if (this.invalidFileType) { return null; }
    this.isUploading = true;
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];

      // eslint-disable-next-line no-await-in-loop
      const key = await this.getUploadForm(file, index);

      if (key) {
        submitResponse.push({
          key,
          file,
        });
      }
    }

    return submitResponse;
  }

  //
  // UPLOAD EVENT
  //
  protected async getUploadForm(file: File, index: number): Promise<string | null> {
    const payload = {
      config: {
        mime: 'any',
      },
    };

    const response: AxiosResponse = await rpcClient('stateless-uploads:form:create', payload)
      .catch((error: AxiosError) => {
        alert(error);
      });

    if (! response || ! response.data || ! response.data.formdata) {
      this.handleError(index);
      return null;
    }

    if (response.status && response.status !== 200) {
      this.handleError(index);
      return null;
    }

    const key = await this.uploadImageToS3(response.data.formdata, file, index);

    if (! key) {
      return null;
    }

    return key;
  }

  protected async uploadImageToS3(formData: any, file: File, index: number): Promise<string | null> {
    if (! formData) { return null; }

    const payload = new FormData();
    Object.keys(formData.inputs).forEach((key: string) => {
      const value = formData.inputs[key];

      if (value) {
        payload.append(key, value);
      }
    });

    payload.append('file', file);
    payload.append('Content-Type', file.type);

    const uploadResponse = await fetch(formData.attributes.action, {
      method: formData.attributes.method,
      body: payload,
    })
      .catch(() => {
        this.handleError(index);
        return null;
      });

    if (! uploadResponse) {
      this.handleError(index);
      return null;
    }

    if ((uploadResponse as Response).status && (uploadResponse as Response).status !== 201) {
      // ERROR
      this.handleError(index);
      return null;
    }

    const responseText = await (uploadResponse as Response).text()
      .catch(() => {
        this.handleError(index);
        return null;
      });

    if (! responseText) {
      this.handleError(index);
      return null;
    }

    const match = responseText.match(/<Key>(.+?)<\/Key>/);
    if (! match || ! match[1]) {
      return null;
    }
    return match[1];
  }

  //
  // EVENTS
  //
  protected async onFileInputChange(): Promise<void> {
    if (this.isLoadingFiles) {
      return;
    }

    if (this.input.files) {
      this.files = Array.from(this.input.files);
      const submitResponse: SubmitResponse[] | null = await this.getFileKey(this.files);

      this.$emit('submitKeys', submitResponse);
    }
  }

  protected handleMouseEnter(): void {
    this.hovering = true;
    this.style = {};
  }

  protected handleMouseLeave(): void {
    this.hovering = false;
  }

  protected handleClickUploaderAction(event: any): void {
    if (this.isLoadingFiles) {
      return;
    }

    const input = this.$refs.hiddenFileInput as HTMLInputElement;
    if (input) {
      this.reset();
      input.value = '';
      const event = new MouseEvent('click');
      input.dispatchEvent(event);
    }
  }

  protected handleDragEnd(event: DragEvent): void {
    this.dragging = false;
  }

  protected handleDragEnter(event: DragEvent): void {
    if (! event.dataTransfer) {
      return;
    }

    this.dragging = true;
    const element: HTMLElement = event.target as HTMLElement;
    this.setRippleStyle(element, event.pageX, event.pageY);

    if (event.dataTransfer.items && event.dataTransfer.items.length && this.accepted.length) {
      const file = Array.from(event.dataTransfer.items)[0];
      this.invalidFileType = ! this.accepted.includes(file.type.toString());
    }
  }

  protected handleDragOver(event: DragEvent): void {
    this.dragging = true;
  }

  protected handleDragLeave(event: DragEvent): void {
    this.reset();
  }

  protected async handleDrop(event: DragEvent): Promise<null | void> {
    if (this.invalidFileType) {
      this.reset();
      return null;
    }

    this.dragging = false;
    if (! event.dataTransfer) {
      return null;
    }

    const files = Array.from(event.dataTransfer.files);
    const keys = await this.getFileKey(files);
    this.$emit('submitKeys', keys);
    this.$emit('submitKeys', files);
  }

  //
  // PARSE FILE TYPE TO ICON & NAME
  //
  private get fileNameFromFilePath() {
    if (! this.filePath) {
      return '';
    }

    const parsedPath = this.filePath.split('/').pop();
    if (! parsedPath) {
      return '';
    }
    return parsedPath.split('?')[0];
  }

  private get fileExtension() {
    return this.fileNameFromFilePath ? this.fileNameFromFilePath.split('.').pop() : null;
  }

  protected get iconClass(): string {
    if (! this.fileExtension || ! this.fileIcons[this.fileExtension]) {
      return this.fileIcons.other;
    }

    return this.fileIcons[this.fileExtension];
  }

  protected get isImage(): boolean {
    if (! this.filePath) {
      return false;
    }

    if (! this.fileExtension) {
      return false;
    }

    return this.imageExtesions.includes(this.fileExtension) || this.filePath.includes('data:image');
  }

  protected get uploadMessage(): string {
    if (! this.dragging) {
      if (this.hovering) {
        return `Click to select one ${this.multiple ? 'or multiple files' : 'file'} `;
      }
      return `Drag one ${this.multiple ? 'or multiple files' : 'file'} to upload`;
    }
    if (this.invalidFileType) {
      return `${this.multiple ? 'One or multiple files are' : 'This file is not supported'} not supported`;
    }
    return `Drop ${this.multiple ? 'one or multiple files' : 'this file'} to upload`;
  }

  protected get rippleStyle(): RippleStyle {
    return this.style;
  }

  private setRippleStyle(element: HTMLElement, x: number, y: number) {
    const boundingRectangle: ClientRect = element.getBoundingClientRect();
    const size = Math.max(boundingRectangle.width, boundingRectangle.height);
    this.style.height = `${size}px`;
    this.style.width = `${size}px`;
    this.style.top = `${y - boundingRectangle.top - size / 2}px`;
    this.style.left = `${x - boundingRectangle.left - size / 2}px`;
  }

  //
  // REMOVE FILE
  //
  protected removeFile(): void {
    this.$emit('fileRemoved');
    this.reset();
  }

  //
  // RESET UPLOAD
  //
  public reset(): void {
    this.dragging = false;
    this.hovering = false;
    this.isUploading = false;
    this.invalidFileType = false;
    this.showRipple = false;
    this.isUploaded = false;
    this.filesUploaded = [];
  }

  //
  // DONE UPLOADING
  //
  public handleUploaded(index: number): void {
    this.filesUploaded[index].isUploaded = true;
    this.filesUploaded[index].isLoading = false;
  }

  //
  // HANDLE ERROR
  //
  public handleError(index: number): void {
    this.filesUploaded[index].isUploaded = false;
    this.filesUploaded[index].isLoading = false;
  }

  //
  // GETTERS
  //
  protected get input(): HTMLInputElement {
    return this.$refs.hiddenFileInput as HTMLInputElement;
  }

  protected get isLoadingFiles(): boolean {
    return !! (this.filesUploaded.length && this.filesUploaded[this.filesUploaded.length - 1].isLoading);
  }

  public uploadedMultiple(index: number): void {
    this.filesUploaded[index].isUploaded = true;
    this.filesUploaded[index].isLoading = false;
  }

  public uploadedMultipleFailed(index: number): void {
    this.filesUploaded[index].isUploaded = false;
    this.filesUploaded[index].isLoading = false;
  }
}

interface RippleStyle {
  top?: string;
  left?: string;
  height?: string;
  width?: string;
}

interface FilesUpload {
  isUploaded?: boolean;
  isLoading?: boolean;
  icon?: string;
}

export interface SubmitResponse {
  file: File;
  key: string;
}
